import { Chip } from "@heroui/react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { useTranslations } from "@/src/i18n/i18n";

/**
 * Une ligne d'effectif — un participant validé ou un postulant.
 *
 * ⚠️ **`id` est un identifiant de compte**, pas de profil : c'est ce que rend
 * le serveur (`row.accountId`) et c'est ce qui ouvre la fiche. Les confondre
 * mène chez quelqu'un d'autre.
 *
 * ⚠️ **`isFlagged` est calculé par l'appelant**, et c'est voulu : la marque
 * n'est pas la même donnée d'une liste à l'autre — `winner` chez les
 * participants, `ghosted` chez les postulants. La rendre ici obligerait ce
 * composant à connaître les deux formes du contrat.
 */
export type RosterMember = {
  id: number;
  username: string;
  avatarUrl: string | null;
  /**
   * L'armée déclarée, telle qu'elle s'affiche.
   *
   * ⚠️ **C'est `gameArmyName` quand le serveur le résout, le slug sinon.** Le
   * nom suit `Accept-Language` et vaut `null` pour une armée retirée du
   * catalogue : dans ce cas le slug reste la seule chose vraie à montrer, et
   * mieux vaut `space_marines` que rien. La bascule se fait chez l'appelant.
   */
  armyLabel: string | null;
  isFlagged: boolean;
};

type RosterListProps = {
  members: RosterMember[];
  /** Ce que dit la pastille des membres marqués (« Vainqueur », « Absent »). */
  flagLabel: string;
  flagColor: "accent" | "warning";
};

/**
 * Une liste de comptes attachés à l'événement, avec leur armée déclarée.
 *
 * Le nom d'armée est résolu **par le serveur** (`gameArmyName`, traduit selon
 * `Accept-Language`) : l'admin ne tient aucun dictionnaire d'armées, qui
 * divergerait dès la première armée ajoutée.
 */
export function RosterList({ members, flagLabel, flagColor }: RosterListProps) {
  const t = useTranslations("events");

  return (
    <ul className="flex flex-col gap-2">
      {members.map((member) => (
        <li
          className="flex flex-wrap items-center gap-x-3 gap-y-1"
          key={member.id}
        >
          <AccountBadge account={member} isLinked />

          {member.armyLabel ? (
            <Chip size="sm" variant="secondary">
              {member.armyLabel}
            </Chip>
          ) : (
            <span className="text-xs text-muted italic">{t("no-army")}</span>
          )}

          {member.isFlagged ? (
            <Chip color={flagColor} size="sm">
              {flagLabel}
            </Chip>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
